import Axios, { AxiosHeaders, AxiosResponse } from "axios";
import { ToastOptions, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

const ToastPosition = 'top-center'

// Request deduplication: prevent duplicate requests within a short time frame
interface PendingRequest {
    promise: Promise<any>;
    timestamp: number;
    timeoutId?: NodeJS.Timeout;
}

const pendingRequests = new Map<string, PendingRequest>();
const DEDUP_WINDOW_MS = 200; // Deduplicate requests within 200ms
const CLEANUP_INTERVAL_MS = 5000; // Clean up old requests every 5 seconds
const DEDUPE_BUFFER_MS = 1000;

// Clean up old requests periodically
setInterval(() => {
    const now = Date.now();
    const keysToDelete: string[] = [];
    // Use Array.from to avoid TypeScript iteration issues
    Array.from(pendingRequests.entries()).forEach(([key, request]) => {
        // Remove requests older than the dedup window + a buffer
        if (now - request.timestamp > DEDUP_WINDOW_MS + DEDUPE_BUFFER_MS) {
            // Clear any pending timeout before deleting
            if (request.timeoutId) {
                clearTimeout(request.timeoutId);
            }
            keysToDelete.push(key);
        }
    });
    keysToDelete.forEach(key => pendingRequests.delete(key));
}, CLEANUP_INTERVAL_MS);

/**
 * Generate a unique key for a request based on method, URL, and payload
 */
function generateRequestKey(method: string, url: string, payload?: any): string {
    // Create a stable string representation of the payload
    let payloadStr = '';
    if (payload !== undefined && payload !== null) {
        try {
            if (typeof payload === 'object' && !Array.isArray(payload)) {
                // Sort object keys to ensure consistent hashing
                const sortedKeys = Object.keys(payload).sort();
                const sortedPayload: any = {};
                sortedKeys.forEach(key => {
                    sortedPayload[key] = payload[key];
                });
                payloadStr = JSON.stringify(sortedPayload);
            } else {
                payloadStr = JSON.stringify(payload);
            }
        } catch {
            payloadStr = String(payload);
        }
    }
    return `${method}:${url}:${payloadStr}`;
}

/**
 * Execute a request with deduplication
 */
async function executeWithDedup<T>(
    method: string,
    url: string,
    requestFn: () => Promise<T>,
    payload?: any
): Promise<T> {
    const key = generateRequestKey(method, url, payload);
    const now = Date.now();

    // Check if there's a pending request for this key
    const existing = pendingRequests.get(key);
    if (existing && (now - existing.timestamp) < DEDUP_WINDOW_MS) {
        // Return the existing promise
        return existing.promise;
    }

    // Create a new request
    const promise = requestFn().finally(() => {
        // Clean up after request completes (with a small delay to allow for rapid duplicates)
        const timeoutId = setTimeout(() => {
            const request = pendingRequests.get(key);
            // Only delete if this timeout is still the active one (hasn't been cleared)
            if (request && request.timeoutId === timeoutId) {
                pendingRequests.delete(key);
            }
        }, DEDUP_WINDOW_MS);

        // Update the stored request with the timeout ID so we can clear it if needed
        const storedRequest = pendingRequests.get(key);
        if (storedRequest) {
            storedRequest.timeoutId = timeoutId;
        }
    });

    // Store the pending request
    pendingRequests.set(key, { promise, timestamp: now });

    return promise;
}

export async function httpGet(url: string, disableToast?: boolean) {
    return executeWithDedup('GET', url, async () => {
        try {
            const result = await Axios.get(`${process.env.REACT_APP_API_URL}/${url}`, {
                headers: {
                    'Authorization': `bearer ${localStorage.getItem(process.env.REACT_APP_ACCESS_TOKEN as string)}`,
                    'Content-Type': 'application/json',
                    Pragma: 'no-cache'
                }
            });
            
            const headers = result.headers;
            if (!disableToast) {
                if (headers && headers instanceof AxiosHeaders && headers.has('X-Response-Message')) {
                    contextualToast(headers);
                }
            }
            
            return result;
        } catch (error: any) {
            if (!disableToast) {
                const headers = error.response?.headers;
                if (headers && headers instanceof AxiosHeaders && headers.has('X-Response-Message')) {
                    contextualToast(headers);
                } else {
                    toast.error(error.response?.statusText || 'An error occurred', { position: ToastPosition });
                }
            }
            return error.response;
        }
    });
}

export async function httpGetBinary(url: string) {
    return executeWithDedup('GET_BINARY', url, async () => {
        try {
            const result = await Axios.get(`${process.env.REACT_APP_API_URL}/${url}`, {
                headers: {
                    'Authorization': `bearer ${localStorage.getItem(process.env.REACT_APP_ACCESS_TOKEN as string)}`,
                    'Content-Type': 'application/json',
                    Pragma: 'no-cache'
                },
                responseType: 'blob'
            });
            return result;
        } catch (error: any) {
            return error.response;
        }
    });
}

export async function httpGetExternal(url: string) {
    return executeWithDedup('GET_EXTERNAL', url, async () => {
        try {
            const result = await Axios.get(`${url}`, {
                headers: {
                    'Content-Type': 'application/json',
                    Pragma: 'no-cache'
                }
            });
            return result;
        } catch (error: any) {
            toast.error(error.response.statusText, { position: ToastPosition });
            return error.response;
        }
    });
}

export async function httpPut(url: string, values: any) {
    return executeWithDedup('PUT', url, async () => {
        try {
            var response = await Axios.put(`${process.env.REACT_APP_API_URL}/${url}`, values, {
                headers: {
                    'Authorization': `bearer ${localStorage.getItem(process.env.REACT_APP_ACCESS_TOKEN as string)}`,
                    'Content-Type': 'application/json'
                }
            });

            const headers = response.headers;
            if (headers instanceof AxiosHeaders && headers.has('X-Response-Message')) {
                contextualToast(headers);
            }

            return response;
        } catch (error: any) {
            toast.error(error.response.statusText, { position: ToastPosition });
            return error.response;
        }
    }, values);
}


export async function httpPost(
    url: string,
    values: any,
    contentType?: string | undefined | null,
    inlineFormErrorCallback?: Function | undefined,
    disableToast?: boolean | undefined,
    axiosConfig?: { signal?: AbortSignal }
) {
    const requestFn = async () => {
        try {
            const config = {
                ...axiosConfig,
                headers: {
                    'Authorization': `bearer ${localStorage.getItem(process.env.REACT_APP_ACCESS_TOKEN as string)}`,
                    'Content-Type': contentType ?? 'application/json'
                }
            };

            const response = await Axios.post(`${process.env.REACT_APP_API_URL}/${url}`, values, config);
            const headers = response.headers;
            if (!disableToast) {
                if (headers instanceof AxiosHeaders && headers.has('X-Response-Message')) {
                    contextualToast(headers);
                }
            }

            return response;
        } catch (error: any) {
            processError(error, inlineFormErrorCallback);
            return error.response;
        }
    };

    // When a signal is provided (e.g. for request cancellation), skip dedup so each request is independent
    if (axiosConfig?.signal) {
        return requestFn();
    }

    const dedupKey = { values, contentType };
    return executeWithDedup('POST', url, requestFn, dedupKey);
}

export async function httpPostWithCompanyId(url: string, values: any) {
    const valuesWithCompanyId = { ...values, companyId: localStorage.getItem("companyId") }
    return executeWithDedup('POST_WITH_COMPANY_ID', url, async () => {
        try {
            const config = {
                headers: {
                    'Authorization': `bearer ${localStorage.getItem(process.env.REACT_APP_ACCESS_TOKEN as string)}`,
                    'Content-Type': 'application/json'
                }
            };

            var response = await Axios.post(`${process.env.REACT_APP_API_URL}/${url}`, valuesWithCompanyId, config)
            const headers = response.headers;
            if (headers instanceof AxiosHeaders && headers.has('X-Response-Message')) {
                contextualToast(headers);
            }

            return response;
        } catch (error: any) {
            toast.error(error.response.statusText, { position: ToastPosition });
            return error.response;
        }
    }, valuesWithCompanyId);
}

export async function httpDelete(url: string, values: any) {
    return executeWithDedup('DELETE', url, async () => {
        try {
            var response = await Axios.delete(`${process.env.REACT_APP_API_URL}/${url}`, {
                headers: {
                    'Authorization': `bearer ${localStorage.getItem(process.env.REACT_APP_ACCESS_TOKEN as string)}`,
                    'Content-Type': 'application/json'
                },
                data: values
            })

            const headers = response.headers;
            if (headers && headers instanceof AxiosHeaders && headers.has('X-Response-Message')) {
                contextualToast(headers);
            } else {
                toast.success('Deleted', { position: ToastPosition });
            }

            return response;
        } catch (error: any) {
            const headers = error.response?.headers;
            if (headers && headers instanceof AxiosHeaders && headers.has('X-Response-Message')) {
                contextualToast(headers);
            } else {
                const errorMessage = error.response?.data?.data?.errors?.[0]
                    ?? error.response?.data?.errors?.[0]
                    ?? error.response?.statusText
                    ?? 'An error occurred';
                toast.error(errorMessage, { position: ToastPosition });
            }
            return error.response;
        }
    }, values);
}

function contextualToast(headers: AxiosHeaders, inlineFormErrorCallback?: Function | undefined) {

    const options: ToastOptions = { position: ToastPosition };
    const message = headers.get('X-Response-Message');
    const responseContext = headers.get('X-Response-Context');

    switch (responseContext) {
        case ResponseContext.Success:
            toast.success(message, options);
            return responseContext;

        case ResponseContext.GlobalError:
            toast.error(message, options);
            return responseContext;

        case ResponseContext.Information:
            toast.info(message, options);
            return responseContext;

        case ResponseContext.InlineError:
            if (inlineFormErrorCallback) {
                inlineFormErrorCallback(message);
            }
            return responseContext;

        default:
            toast.success(message, options);
            return ResponseContext.Success;
    }
}

export const ResponseContext = {
    Success: "Success",
    GlobalError: "GlobalError",
    InlineError: "InlineError",
    Information: "Information",
}

const processError = (error: any, inlineFormErrorCallback?: Function | undefined) => {

    const errorPayload = error.response?.data?.data || error.response?.data;

    // The strongly typed bad request IAction result is causing a different shaped response json
    // Seems related to the fact that we have to pass null into the data param due to lack of new constraint on the type argument
    // Have put in a fallback to make sure no issues for the meantime
    const errorMessage = error.response?.data?.data?.errors?.[0] ||
        error.response?.data?.errors?.[0] ||
        error.response?.statusText;

    if (errorPayload.responseContext === ResponseContext.GlobalError) {
        toast.error(errorMessage, { position: ToastPosition });
    } else {
        if (inlineFormErrorCallback) {
            inlineFormErrorCallback(errorMessage);
        }
    }

    return errorMessage;
}
